/**
 * Logger Service
 * Production-safe logging with environment-based configuration
 * Replaces console.log statements throughout the application
 */

import { auditService } from './auditService';
import { AuditAction, AuditResource, AuditSeverity } from '../types/audit';

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  data?: any;
}

class LoggerService {
  private isDevelopment: boolean;
  private enableConsoleLogs: boolean;
  private logBuffer: any[] = [];

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.enableConsoleLogs = process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true' || this.isDevelopment;
  }

  /**
   * Format log message with timestamp and context
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const component = context?.component || 'App';
    return `[${timestamp}] [${level}] [${component}] ${message}`;
  }

  /**
   * Log to console in development mode only
   */
  private logToConsole(level: LogLevel, message: string, data?: any) {
    if (!this.enableConsoleLogs) return;

    const formattedMessage = this.formatMessage(level, message);
    
    switch (level) {
      case LogLevel.DEBUG:
        console.log(formattedMessage, data || '');
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, data || '');
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, data || '');
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, data || '');
        break;
    }
  }

  /**
   * Send error to monitoring service (Sentry, LogRocket, etc.)
   */
  private async sendToMonitoring(level: LogLevel, message: string, error?: any, context?: LogContext) {
    // In production, send to error monitoring service
    // if (window.Sentry && level === LogLevel.ERROR) {
    //   window.Sentry.captureException(error, {
    //     level: 'error',
    //     tags: { component: context?.component },
    //     extra: { message, context }
    //   });
    // }

    // For now, just buffer critical errors
    if (level === LogLevel.ERROR) {
      this.logBuffer.push({
        timestamp: new Date(),
        level,
        message,
        error,
        context,
      });

      // Keep only last 100 errors
      if (this.logBuffer.length > 100) {
        this.logBuffer.shift();
      }
    }
  }

  /**
   * Debug logging (development only)
   */
  debug(message: string, data?: any, context?: LogContext) {
    this.logToConsole(LogLevel.DEBUG, message, data);
  }

  /**
   * Info logging
   */
  info(message: string, data?: any, context?: LogContext) {
    this.logToConsole(LogLevel.INFO, message, data);
  }

  /**
   * Warning logging
   */
  warn(message: string, data?: any, context?: LogContext) {
    this.logToConsole(LogLevel.WARN, message, data);
    this.sendToMonitoring(LogLevel.WARN, message, data, context);
  }

  /**
   * Error logging with monitoring
   */
  error(message: string, error?: any, context?: LogContext) {
    this.logToConsole(LogLevel.ERROR, message, error);
    this.sendToMonitoring(LogLevel.ERROR, message, error, context);
  }

  /**
   * Firebase operation logging
   */
  firebaseOperation(operation: string, collection: string, success: boolean, error?: any) {
    const message = `Firebase ${operation} on ${collection}`;
    
    if (success) {
      this.debug(message, { operation, collection });
    } else {
      this.error(message, error, { component: 'Firebase', action: operation, data: { collection } });
    }
  }

  /**
   * API request logging
   */
  apiRequest(method: string, endpoint: string, status?: number, error?: any) {
    const message = `API ${method} ${endpoint}`;
    
    if (status && status >= 200 && status < 300) {
      this.debug(message, { method, endpoint, status });
    } else if (error) {
      this.error(message, error, { component: 'API', action: method, data: { endpoint } });
    }
  }

  /**
   * User action logging (with audit trail)
   */
  async userAction(params: {
    action: string;
    resource: string;
    resourceId: string;
    user: { id: string; email: string; name: string; role: string };
    success: boolean;
    error?: any;
    data?: any;
  }) {
    const message = `User ${params.user.email} performed ${params.action} on ${params.resource}`;
    
    if (params.success) {
      this.info(message, params.data);
    } else {
      this.error(message, params.error, { 
        component: params.resource, 
        action: params.action,
        userId: params.user.id,
        data: params.data 
      });
    }

    // Note: Actual audit logging should be done through auditService
    // This is just for console/monitoring
  }

  /**
   * Performance logging
   */
  performance(operation: string, durationMs: number, context?: LogContext) {
    const message = `Performance: ${operation} took ${durationMs}ms`;
    
    if (durationMs > 3000) {
      this.warn(message, { operation, durationMs }, context);
    } else {
      this.debug(message, { operation, durationMs });
    }
  }

  /**
   * Get recent error logs (for debugging)
   */
  getRecentErrors(count: number = 10): any[] {
    return this.logBuffer.slice(-count);
  }

  /**
   * Clear error buffer
   */
  clearErrorBuffer() {
    this.logBuffer = [];
  }
}

export const logger = new LoggerService();
